import type {
  Issue,
  IssueInput,
  Publisher,
  PublisherInput,
  QuickAddRequest,
  QuickAddResponse,
  CompleteSeriesResponse,
  SeriesDetail,
  SeriesInput,
  SeriesListItem,
  SeriesListQuery,
} from '@panelvault/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, toQuery } from '../../lib/api';

interface Page<T> {
  items: T[];
  total: number;
  page: number;
  perPage: number;
}

export type SeriesListParams = Partial<SeriesListQuery>;

export const catalogKeys = {
  publishers: ['publishers'] as const,
  languages: ['series', 'languages'] as const,
  seriesList: (params: SeriesListParams) => ['series', 'list', params] as const,
  seriesDetail: (id: string) => ['series', 'detail', id] as const,
};

export function usePublishers() {
  return useQuery({
    queryKey: catalogKeys.publishers,
    queryFn: () => api.get<Publisher[]>('/publishers'),
    staleTime: 60_000,
  });
}

export function useLanguages() {
  return useQuery({
    queryKey: catalogKeys.languages,
    queryFn: () => api.get<string[]>('/series/languages'),
    staleTime: 60_000,
  });
}

export function useSeriesList(params: SeriesListParams) {
  return useQuery({
    queryKey: catalogKeys.seriesList(params),
    queryFn: () => api.get<Page<SeriesListItem>>(`/series${toQuery(params)}`),
    placeholderData: (previous) => previous,
  });
}

export function useSeriesDetail(id: string | undefined) {
  return useQuery({
    queryKey: catalogKeys.seriesDetail(id ?? ''),
    queryFn: () => api.get<SeriesDetail>(`/series/${id}`),
    enabled: Boolean(id),
    // While the run loads in the background (after a scan), new volumes and
    // covers appear without a reload.
    refetchInterval: (query) => (query.state.data?.completing ? 3000 : false),
  });
}

/**
 * Loads a series before it is opened (on hover or touch), so the detail page
 * is there at once and the cover can fly into place.
 */
export function usePrefetchSeries() {
  const queryClient = useQueryClient();
  return (id: string) =>
    void queryClient.prefetchQuery({
      queryKey: catalogKeys.seriesDetail(id),
      queryFn: () => api.get<SeriesDetail>(`/series/${id}`),
      staleTime: 30_000,
    });
}

/** Any catalogue change can move the collection and the dashboard numbers. */
function useCatalogInvalidation() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: ['series'] });
    void queryClient.invalidateQueries({ queryKey: ['collection'] });
    void queryClient.invalidateQueries({ queryKey: ['stats'] });
    void queryClient.invalidateQueries({ queryKey: ['wishlist'] });
  };
}

export function useCreatePublisher() {
  const invalidate = useCatalogInvalidation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: PublisherInput) => api.post<Publisher>('/publishers', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: catalogKeys.publishers });
      invalidate();
    },
  });
}

export function useCreateSeries() {
  const invalidate = useCatalogInvalidation();
  return useMutation({
    mutationFn: (input: SeriesInput) => api.post<SeriesListItem>('/series', input),
    onSuccess: invalidate,
  });
}

export function useUpdateSeries() {
  const invalidate = useCatalogInvalidation();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<SeriesInput> }) =>
      api.patch<SeriesListItem>(`/series/${id}`, patch),
    onSuccess: invalidate,
  });
}

export function useDeleteSeries() {
  const invalidate = useCatalogInvalidation();
  return useMutation({
    mutationFn: (id: string) => api.delete<{ ok: true }>(`/series/${id}`),
    onSuccess: invalidate,
  });
}

export function useQuickAdd() {
  const invalidate = useCatalogInvalidation();
  return useMutation({
    mutationFn: ({ seriesId, input }: { seriesId: string; input: QuickAddRequest }) =>
      api.post<QuickAddResponse>(`/series/${seriesId}/quick-add`, input),
    onSuccess: invalidate,
  });
}

export function useNextIssue() {
  const invalidate = useCatalogInvalidation();
  return useMutation({
    mutationFn: (seriesId: string) => api.post<Issue>(`/series/${seriesId}/next-issue`),
    onSuccess: invalidate,
  });
}

export function useCompleteSeries() {
  const invalidate = useCatalogInvalidation();
  return useMutation({
    mutationFn: (seriesId: string) =>
      api.post<CompleteSeriesResponse>(`/series/${seriesId}/complete`),
    onSuccess: invalidate,
  });
}

export function useWishMissing() {
  const invalidate = useCatalogInvalidation();
  return useMutation({
    mutationFn: ({ seriesId, numbers }: { seriesId: string; numbers: string[] }) =>
      api.post<{ added: number }>(`/series/${seriesId}/wish-missing`, { numbers }),
    onSuccess: invalidate,
  });
}

export function useCreateIssue() {
  const invalidate = useCatalogInvalidation();
  return useMutation({
    mutationFn: (input: IssueInput) => api.post<Issue>('/issues', input),
    onSuccess: invalidate,
  });
}

export function useUpdateIssue() {
  const invalidate = useCatalogInvalidation();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<IssueInput> }) =>
      api.patch<Issue>(`/issues/${id}`, patch),
    onSuccess: invalidate,
  });
}

export function useDeleteIssue() {
  const invalidate = useCatalogInvalidation();
  return useMutation({
    mutationFn: (id: string) => api.delete<{ ok: true }>(`/issues/${id}`),
    onSuccess: invalidate,
  });
}
